import {Radio, RadioGroup} from "@headlessui/react";

export type OtsRadioOption<T extends string> = {value: T; label: string; disabled?: boolean};
export type OtsRadioGroupProps<T extends string> = {
  options: readonly OtsRadioOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  size?: "compact" | "default";
  disabled?: boolean;
  className?: string;
};

/** Single-choice settings with roving focus, arrow-key selection and theme-aware radio indicators. */
export function OtsRadioGroup<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  size = "default",
  disabled,
  className,
}: OtsRadioGroupProps<T>) {
  return (
    <RadioGroup
      value={value}
      onChange={onChange}
      disabled={disabled}
      aria-label={ariaLabel}
      className={["flex flex-col gap-1", className].filter(Boolean).join(" ")}
    >
      {options.map((option) => (
        <Radio
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          aria-label={option.label}
          className={[
            "group flex cursor-pointer items-center gap-2.5 rounded-md text-[var(--ots-text)] outline-none hover:bg-[var(--ots-surface-variant)]",
            "data-[focus]:ring-2 data-[focus]:ring-[var(--ots-primary)] data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
            size === "compact" ? "px-2.5 py-2 text-xs" : "px-3 py-2.5 text-sm",
          ].join(" ")}
        >
          <span
            aria-hidden="true"
            className="flex size-4 shrink-0 items-center justify-center rounded-full border border-[var(--ots-outline)] bg-[var(--ots-field)] group-data-[checked]:border-[var(--ots-primary)]"
          >
            <span className="size-2 rounded-full bg-[var(--ots-primary)] opacity-0 group-data-[checked]:opacity-100" />
          </span>
          <span>{option.label}</span>
        </Radio>
      ))}
    </RadioGroup>
  );
}
